import { VirtualAssistantNavTarget } from '@shinetools/virtual-assistant-client'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { EmberRoute } from '../../../enums/emberRoute'
import { useEmberTransitionTo } from '../../../hooks/routing/useEmberTransitionTo'
import { getOrganizationViewUrl } from '../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../utils/routing/reactRoute'

type VirtualAssistantRouteResult = { type: 'react'; url: string } | { type: 'ember'; route: EmberRoute; id?: string }

const targetsRequiringId: ReadonlySet<VirtualAssistantNavTarget> = new Set([
  VirtualAssistantNavTarget.BillView,
  VirtualAssistantNavTarget.ClientView,
  VirtualAssistantNavTarget.InvoiceView,
  VirtualAssistantNavTarget.QuoteView,
  VirtualAssistantNavTarget.VatDeclarationView,
])

const getRouteMap = (orgUrl: string, id?: string): Record<VirtualAssistantNavTarget, VirtualAssistantRouteResult> => ({
  [VirtualAssistantNavTarget.Dashboard]: {
    type: 'react',
    url: getOrganizationViewUrl(orgUrl, reactRoute.organization.dashboard.getMainRoute()),
  },
  [VirtualAssistantNavTarget.BillsList]: {
    type: 'react',
    url: getOrganizationViewUrl(orgUrl, reactRoute.organization.bills.getMainRoute()),
  },
  [VirtualAssistantNavTarget.BillNew]: {
    type: 'react',
    url: getOrganizationViewUrl(orgUrl, reactRoute.organization.bills.getNewBillRoute()),
  },
  [VirtualAssistantNavTarget.BillView]: {
    type: 'react',
    url: getOrganizationViewUrl(orgUrl, reactRoute.organization.bills.getViewBillRoute(id)),
  },
  [VirtualAssistantNavTarget.InvoicesList]: {
    type: 'react',
    url: getOrganizationViewUrl(orgUrl, reactRoute.organization.invoices.getMainRoute()),
  },
  [VirtualAssistantNavTarget.QuotesList]: {
    type: 'react',
    url: getOrganizationViewUrl(orgUrl, reactRoute.organization.quotes.getMainRoute()),
  },
  [VirtualAssistantNavTarget.ClientsList]: {
    type: 'react',
    url: getOrganizationViewUrl(orgUrl, reactRoute.organization.clients.getMainRoute()),
  },
  [VirtualAssistantNavTarget.ClientView]: {
    type: 'react',
    url: getOrganizationViewUrl(orgUrl, reactRoute.organization.clients.getViewClientRoute(id)),
  },
  [VirtualAssistantNavTarget.SuppliersList]: {
    type: 'react',
    url: getOrganizationViewUrl(orgUrl, reactRoute.organization.suppliers.getMainRoute()),
  },
  [VirtualAssistantNavTarget.Transactions]: {
    type: 'react',
    url: getOrganizationViewUrl(orgUrl, 'transactions'),
  },
  [VirtualAssistantNavTarget.VatDeclarationsList]: {
    type: 'react',
    url: getOrganizationViewUrl(orgUrl, reactRoute.organization.vatDeclarations.getMainRoute()),
  },
  [VirtualAssistantNavTarget.VatDeclarationView]: {
    type: 'react',
    url: getOrganizationViewUrl(orgUrl, reactRoute.organization.vatDeclarations.getViewVatDeclarationRoute(id)),
  },
  [VirtualAssistantNavTarget.Reports]: {
    type: 'react',
    url: getOrganizationViewUrl(orgUrl, reactRoute.organization.reports.getMainRoute()),
  },
  [VirtualAssistantNavTarget.ReportsProfitAndLoss]: {
    type: 'react',
    url: getOrganizationViewUrl(orgUrl, reactRoute.organization.reports.getProfitAndLossRoute()),
  },
  [VirtualAssistantNavTarget.ReportsBalance]: {
    type: 'react',
    url: getOrganizationViewUrl(orgUrl, reactRoute.organization.reports.getBalanceSheetRoute()),
  },
  [VirtualAssistantNavTarget.ReportsTrialBalance]: {
    type: 'react',
    url: getOrganizationViewUrl(orgUrl, reactRoute.organization.reports.getTrialBalanceRoute()),
  },
  [VirtualAssistantNavTarget.BankAccounts]: {
    type: 'react',
    url: getOrganizationViewUrl(orgUrl, reactRoute.organization.bank.getBankAccountsRoute()),
  },
  [VirtualAssistantNavTarget.Uploads]: {
    type: 'react',
    url: getOrganizationViewUrl(orgUrl, reactRoute.organization.uploads.getMainRoute()),
  },
  [VirtualAssistantNavTarget.Exports]: {
    type: 'react',
    url: getOrganizationViewUrl(orgUrl, reactRoute.organization.exports.getMainRoute()),
  },
  [VirtualAssistantNavTarget.Creditors]: {
    type: 'react',
    url: getOrganizationViewUrl(orgUrl, reactRoute.organization.creditors.getMainRoute()),
  },
  [VirtualAssistantNavTarget.Debtors]: {
    type: 'react',
    url: getOrganizationViewUrl(orgUrl, reactRoute.organization.debtors.getMainRoute()),
  },

  [VirtualAssistantNavTarget.InvoiceView]: {
    type: 'ember',
    route: EmberRoute.Invoice,
    id,
  },
  [VirtualAssistantNavTarget.SettingsOrganization]: { type: 'ember', route: EmberRoute.SettingsOrganization },
  [VirtualAssistantNavTarget.SettingsOrganizationAccounting]: {
    type: 'ember',
    route: EmberRoute.SettingsAccounting,
  },
  [VirtualAssistantNavTarget.SettingsOrganizationVatReturn]: {
    type: 'ember',
    route: EmberRoute.SalesTaxReturnSettings,
  },
  [VirtualAssistantNavTarget.SettingsOrganizationChartOfAccounts]: {
    type: 'react',
    url: getOrganizationViewUrl(orgUrl, reactRoute.organization.chartOfAccounts.getMainRoute()),
  },
  [VirtualAssistantNavTarget.SettingsOrganizationInvoicing]: {
    type: 'ember',
    route: EmberRoute.SettingsOrganizationInvoicing,
  },
  [VirtualAssistantNavTarget.SettingsOrganizationUsers]: {
    type: 'ember',
    route: EmberRoute.SettingsOrganizationUsers,
  },
  [VirtualAssistantNavTarget.SettingsOrganizationSubscription]: {
    type: 'ember',
    route: EmberRoute.SettingsSubscription,
  },
  [VirtualAssistantNavTarget.SettingsOrganizationVatRates]: { type: 'ember', route: EmberRoute.SettingsVatRates },
  [VirtualAssistantNavTarget.SettingsOrganizationAccessTokens]: {
    type: 'ember',
    route: EmberRoute.SettingsAccessTokens,
  },
  [VirtualAssistantNavTarget.SettingsOrganizationBetas]: {
    type: 'ember',
    route: EmberRoute.SettingsOrganizationFeatures,
  },
  [VirtualAssistantNavTarget.SettingsUserProfile]: { type: 'ember', route: EmberRoute.SettingsUser },
  [VirtualAssistantNavTarget.SettingsUserNotifications]: {
    type: 'ember',
    route: EmberRoute.SettingsNotification,
  },
  [VirtualAssistantNavTarget.SettingsUserPrivacy]: { type: 'ember', route: EmberRoute.SettingsPrivacy },
  [VirtualAssistantNavTarget.SettingsUserOrganizations]: {
    type: 'ember',
    route: EmberRoute.SettingsOrganizations,
  },
  [VirtualAssistantNavTarget.QuoteView]: { type: 'ember', route: EmberRoute.Quote, id },
})

export const useVirtualAssistantNavigate = () => {
  const routerNavigate = useNavigate()
  const { organization } = useUserOrganization()
  const { transitionTo } = useEmberTransitionTo()

  const navigate = useCallback(
    (target: VirtualAssistantNavTarget, id?: string): boolean => {
      if (!organization) {
        return false
      }

      if (targetsRequiringId.has(target) && !id) {
        return false
      }

      const result = getRouteMap(organization.url, id)[target]
      if (!result) {
        return false
      }

      if (result.type === 'react') {
        routerNavigate(result.url)
      } else {
        transitionTo(result.route, ...(result.id ? [result.id] : []))
      }

      return true
    },
    [organization, routerNavigate, transitionTo],
  )

  return useMemo(() => ({ navigate }), [navigate])
}
